import { useState } from "react";
import { Input } from "@base-ui/react/input";
import { Dialog } from "./Dialog";
import { Button } from "./Button";
import {
  type ModelConfig,
  type ProviderId,
  type ProviderKeyStatus,
  APPROVED_MODEL_CATALOG,
  FAMILY_OPTIONS,
  PROVIDER_KEY_DOCS,
  PROVIDER_KEY_PLACEHOLDERS,
  PROVIDER_LABELS,
  getParseUnsupportedReason,
  isParseFamilySupported,
} from "../lib/model-catalog";

const PROVIDER_ORDER: ProviderId[] = ["anthropic", "openai", "google"];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  providerKeys: ProviderKeyStatus;
  onSaveProviderKey: (provider: ProviderId, key: string) => Promise<void>;
  onClearData: () => Promise<void>;
  modelConfig: ModelConfig;
  onSaveModelConfig: (config: ModelConfig) => Promise<void>;
}

export function SettingsModal({
  isOpen,
  onClose,
  providerKeys,
  onSaveProviderKey,
  onClearData,
  modelConfig,
  onSaveModelConfig,
}: Props) {
  const [keyInputs, setKeyInputs] = useState<Record<ProviderId, string>>({
    anthropic: "",
    openai: "",
    google: "",
  });
  const [savingKey, setSavingKey] = useState<ProviderId | null>(null);
  const [isClearing, setIsClearing] = useState(false);
  const [error, setError] = useState("");
  const [savingModel, setSavingModel] = useState<"answer" | "parse" | "utilities" | null>(null);

  async function handleSaveProviderKey(provider: ProviderId) {
    const apiKey = keyInputs[provider].trim();
    if (!apiKey) return;

    setSavingKey(provider);
    setError("");
    try {
      await onSaveProviderKey(provider, apiKey);
      setKeyInputs((prev) => ({ ...prev, [provider]: "" }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save API key");
    } finally {
      setSavingKey(null);
    }
  }

  async function handleClearData() {
    setIsClearing(true);
    setError("");
    try {
      await onClearData();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to clear data");
    } finally {
      setIsClearing(false);
    }
  }

  async function handleModelChange(
    field: "answer" | "parse" | "utilities",
    value: string,
  ) {
    setSavingModel(field);
    setError("");
    try {
      const next: ModelConfig =
        field === "answer"
          ? { ...modelConfig, answerFamily: value as ModelConfig["answerFamily"] }
          : field === "parse"
            ? { ...modelConfig, parseFamily: value as ModelConfig["parseFamily"] }
            : {
                ...modelConfig,
                utilitiesOverrideFamily: value
                  ? (value as ModelConfig["answerFamily"])
                  : null,
              };

      await onSaveModelConfig(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save model config");
    } finally {
      setSavingModel(null);
    }
  }

  function handleClose() {
    setKeyInputs({ anthropic: "", openai: "", google: "" });
    setError("");
    onClose();
  }

  const parseSupportReason = getParseUnsupportedReason(modelConfig.parseFamily);
  const utilityParseSupportReason =
    modelConfig.utilitiesOverrideFamily &&
    !isParseFamilySupported(modelConfig.utilitiesOverrideFamily)
      ? `Utilities override (${modelConfig.utilitiesOverrideFamily}) does not support parsing utilities.`
      : "";

  return (
    <Dialog open={isOpen} onClose={handleClose} title="Settings">
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-3">Provider API Keys</label>
          <div className="space-y-3">
            {PROVIDER_ORDER.map((provider) => (
              <div
                key={provider}
                className="rounded-lg border border-(--color-border) bg-(--color-bg-subtle) p-3"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium flex items-center gap-2">
                    <span
                      className={`inline-block w-2 h-2 rounded-full ${providerKeys[provider] ? "bg-green-500" : "bg-(--color-text-tertiary)"}`}
                    />
                    {PROVIDER_LABELS[provider]}
                  </div>
                  <a
                    href={PROVIDER_KEY_DOCS[provider]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs underline text-(--color-text-muted) hover:text-(--color-text)"
                  >
                    Get key
                  </a>
                </div>

                <Input
                  type="password"
                  value={keyInputs[provider]}
                  onChange={(e) =>
                    setKeyInputs((prev) => ({ ...prev, [provider]: e.target.value }))
                  }
                  placeholder={PROVIDER_KEY_PLACEHOLDERS[provider]}
                  autoComplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                  className="w-full px-3 py-2 text-sm bg-(--color-bg-muted) border border-(--color-border) rounded-lg focus:outline-none focus:border-(--color-text-muted)"
                />

                {keyInputs[provider].trim() && (
                  <Button
                    onClick={() => handleSaveProviderKey(provider)}
                    disabled={savingKey === provider}
                    size="sm"
                    className="mt-2"
                  >
                    {savingKey === provider ? "Saving..." : `Save ${PROVIDER_LABELS[provider]} key`}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="pt-4 border-t border-(--color-border)">
          <label className="block text-sm font-medium mb-3">Advanced AI</label>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-(--color-text-muted) mb-1">
                Answer model family
              </label>
              <select
                value={modelConfig.answerFamily}
                onChange={(e) => handleModelChange("answer", e.target.value)}
                disabled={savingModel === "answer"}
                className="w-full px-3 py-2 text-sm bg-(--color-bg-muted) border border-(--color-border) rounded-lg focus:outline-none focus:border-(--color-text-muted)"
              >
                {FAMILY_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs text-(--color-text-muted) mb-1">
                Parse model family
              </label>
              <select
                value={modelConfig.parseFamily}
                onChange={(e) => handleModelChange("parse", e.target.value)}
                disabled={savingModel === "parse"}
                className="w-full px-3 py-2 text-sm bg-(--color-bg-muted) border border-(--color-border) rounded-lg focus:outline-none focus:border-(--color-text-muted)"
              >
                {FAMILY_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
              {parseSupportReason && (
                <p className="mt-1 text-xs text-amber-700">{parseSupportReason}</p>
              )}
            </div>

            <div>
              <label className="block text-xs text-(--color-text-muted) mb-1">
                Utilities override (optional)
              </label>
              <select
                value={modelConfig.utilitiesOverrideFamily ?? ""}
                onChange={(e) => handleModelChange("utilities", e.target.value)}
                disabled={savingModel === "utilities"}
                className="w-full px-3 py-2 text-sm bg-(--color-bg-muted) border border-(--color-border) rounded-lg focus:outline-none focus:border-(--color-text-muted)"
              >
                <option value="">Follow task model (default)</option>
                {FAMILY_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
              {utilityParseSupportReason && (
                <p className="mt-1 text-xs text-amber-700">{utilityParseSupportReason}</p>
              )}
            </div>
          </div>

          <p className="mt-3 text-xs text-(--color-text-muted)">
            Catalog: {APPROVED_MODEL_CATALOG.sonnet.map((m) => m.label).join(", ")}; {APPROVED_MODEL_CATALOG.codex.map((m) => m.label).join(", ")}; {APPROVED_MODEL_CATALOG.gemini.map((m) => m.label).join(", ")}.
          </p>
        </div>

        <div className="pt-4 border-t border-(--color-border)">
          <label className="block text-sm font-medium mb-2">Data Management</label>
          <Button
            variant="danger-outline"
            size="sm"
            onClick={handleClearData}
            disabled={isClearing}
          >
            {isClearing ? "Resetting" : "Reset data"}
          </Button>
        </div>

        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    </Dialog>
  );
}
